import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import clsx from 'clsx';

import { api } from '../api';
import type { Me } from '../App';
import ConfirmDialog from '../components/ConfirmDialog';
import CalendarKeywordEditor from '../components/CalendarKeywordEditor';
import Section from '../components/SettingsSection';
import WeatherSettings from '../components/WeatherSettings';
import { calendarPushStatus, type Account, type Calendar, type CalendarEvent, type Preferences } from '../types';
import {
  applyTheme,
  LOCAL_THEME_CACHE_KEY,
  retintEventBackground,
  type ThemeChoice,
  useResolvedTheme
} from '../theme';

type Theme = ThemeChoice;

type ThemeDef = { id: Theme; name: string; bg: string; text: string; accent: string };

const THEMES: ThemeDef[] = [
  { id: 'light', name: 'Light', bg: '#ffffff', text: '#1f1f1e', accent: '#2563eb' },
  { id: 'dark', name: 'Dark', bg: '#191919', text: '#e6e6e3', accent: '#3b82f6' },
  { id: 'auto', name: 'Auto', bg: '#ffffff', text: '#1f1f1e', accent: '#2563eb' },
  { id: 'synthwave84', name: "Synthwave '84", bg: '#2a2139', text: '#f4eee4', accent: '#ff7edb' },
  { id: 'solarized-light', name: 'Solarized Light', bg: '#fdf6e3', text: '#586e75', accent: '#268bd2' },
  { id: 'solarized-dark', name: 'Solarized Dark', bg: '#002b36', text: '#93a1a1', accent: '#268bd2' },
  { id: 'dracula', name: 'Dracula', bg: '#282a36', text: '#f8f8f2', accent: '#bd93f9' },
  { id: 'nord', name: 'Nord', bg: '#2e3440', text: '#eceff4', accent: '#88c0d0' },
  { id: 'gruvbox-dark', name: 'Gruvbox Dark', bg: '#282828', text: '#ebdbb2', accent: '#fabd2f' },
  { id: 'gruvbox-light', name: 'Gruvbox Light', bg: '#fbf1c7', text: '#3c3836', accent: '#b57614' },
  { id: 'tokyo-night', name: 'Tokyo Night', bg: '#1a1b26', text: '#c0caf5', accent: '#7aa2f7' },
  { id: 'catppuccin-mocha', name: 'Catppuccin Mocha', bg: '#1e1e2e', text: '#cdd6f4', accent: '#cba6f7' },
  { id: 'catppuccin-macchiato', name: 'Catppuccin Macchiato', bg: '#24273a', text: '#cad3f5', accent: '#c6a0f6' },
  { id: 'catppuccin-latte', name: 'Catppuccin Latte', bg: '#eff1f5', text: '#4c4f69', accent: '#8839ef' },
  { id: 'rose-pine', name: 'Rosé Pine', bg: '#191724', text: '#e0def4', accent: '#c4a7e7' },
  { id: 'monokai', name: 'Monokai', bg: '#272822', text: '#f8f8f2', accent: '#a6e22e' },
  { id: 'venice', name: 'Venice', bg: '#faf3e3', text: '#3b2e1e', accent: '#2d7a7e' },
  { id: 'venetian-night', name: 'Venetian Night', bg: '#0e1a26', text: '#f1e4c4', accent: '#d8a93b' }
];

// Mirrored from routes/google.py — fixed-vocabulary error codes from the
// add-account OAuth callback. Anything outside this map falls through to a
// generic message; never echo the raw param.
const OAUTH_ERR_COPY: Record<string, string> = {
  oauth_state: 'Add-account sign-in expired before it could complete. Try again.',
  oauth_failed: 'Adding the Google account failed. Please try again.',
  not_allowed: 'That Google account is not permitted.'
};

type TabId = 'general' | 'calendars' | 'weather' | 'accounts';
const TABS: { id: TabId; label: string }[] = [
  { id: 'general', label: 'General' },
  { id: 'calendars', label: 'Calendars' },
  { id: 'weather', label: 'Weather' },
  { id: 'accounts', label: 'Accounts' }
];

export default function Settings() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const themeName = useResolvedTheme();
  const [params, setParams] = useSearchParams();
  const oauthErr = params.get('err');
  const oauthErrMsg = oauthErr ? OAUTH_ERR_COPY[oauthErr] ?? 'Sign-in failed.' : null;

  // OAuth errors come back from the add-account flow, so land the user on the
  // tab where the offending account lives.
  const tabParam = params.get('tab');
  const activeTab: TabId = TABS.some((t) => t.id === tabParam)
    ? (tabParam as TabId)
    : oauthErr
    ? 'accounts'
    : 'general';
  function setTab(id: TabId) {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', id);
        next.delete('err');
        return next;
      },
      { replace: true }
    );
  }

  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);
  const [confirmSpread, setConfirmSpread] = useState(false);

  const meQ = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<Me>('/api/me')
  });
  const identityAccountId = meQ.data?.identity_account_id ?? null;
  const pushConfigured = !!meQ.data?.push_configured;
  const accountsQ = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get<Account[]>('/api/accounts')
  });
  const prefsQ = useQuery({
    queryKey: ['preferences'],
    queryFn: () => api.get<Preferences>('/api/preferences')
  });
  const calsQ = useQuery({
    queryKey: ['calendars'],
    queryFn: () => api.get<Calendar[]>('/api/calendars')
  });
  const writableCalendars = (calsQ.data ?? []).filter(
    (c) => c.access_role === 'owner' || c.access_role === 'writer'
  );

  const autoSpread = useMutation({
    mutationFn: () => api.post<{ ok: boolean; updated: number }>('/api/calendars/auto-spread'),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['calendars'] });
      qc.invalidateQueries({ queryKey: ['events'] });
    }
  });

  const setInSidebar = useMutation({
    mutationFn: ({ id, in_sidebar }: { id: string; in_sidebar: boolean }) =>
      api.patch(`/api/calendars/${id}`, { in_sidebar }),
    onMutate: ({ id, in_sidebar }) => {
      qc.setQueryData<Calendar[]>(['calendars'], (old) =>
        old ? old.map((c) => (c.id === id ? { ...c, in_sidebar } : c)) : old
      );
      if (!in_sidebar) {
        qc.setQueriesData<CalendarEvent[]>({ queryKey: ['events'] }, (old) =>
          old ? old.filter((ev) => ev.calendar_id !== id) : old
        );
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['calendars'] });
      qc.invalidateQueries({ queryKey: ['events'] });
    }
  });

  const updatePrefs = useMutation({
    mutationFn: (patch: Partial<Preferences>) =>
      api.patch<Preferences>('/api/preferences', patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['preferences'] });
      qc.invalidateQueries({ queryKey: ['events'] });
    }
  });

  const weekStartsOn = prefsQ.data?.week_starts_on ?? 0;
  // Theme falls back to the localStorage hint (also set by theme-init.js)
  // while preferences are loading, so the radio shows the right choice from
  // the first render instead of flickering through 'light'.
  const theme: Theme =
    (prefsQ.data?.theme as Theme | undefined) ??
    ((typeof localStorage !== 'undefined'
      ? (localStorage.getItem(LOCAL_THEME_CACHE_KEY) as Theme | null)
      : null) ??
      'light');
  function setTheme(next: Theme) {
    applyTheme(next);
    updatePrefs.mutate({ theme: next });
  }

  const disconnect = useMutation({
    mutationFn: (account_id: string) => api.post('/google/disconnect', { account_id }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['calendars'] });
      qc.invalidateQueries({ queryKey: ['events'] });
    }
  });

  async function logout() {
    await api.post('/auth/logout');
    qc.clear();
    window.location.href = '/login';
  }

  return (
    <div className="mx-auto max-w-[720px] px-6 pb-16 pt-8">
      <header className="mb-8 flex items-center justify-between">
        <button
          onClick={() => nav('/')}
          className="text-sm text-[var(--text-muted)] hover:text-[var(--text)]"
        >
          ‹ Calendar
        </button>
        <h1 className="m-0 text-xl font-semibold">Settings</h1>
        <button onClick={logout} className="text-sm text-[var(--text-muted)] hover:text-[var(--today-fg)]">
          Sign out
        </button>
      </header>

      <nav
        role="tablist"
        aria-label="Settings sections"
        className="mb-8 inline-flex rounded-md bg-[var(--bg-muted)] p-[2px]"
      >
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={activeTab === t.id}
            onClick={() => setTab(t.id)}
            className={clsx(
              'rounded px-4 py-[6px] text-[0.85rem]',
              activeTab === t.id
                ? 'bg-[var(--bg)] text-[var(--text)] shadow-sm'
                : 'text-[var(--text-muted)] hover:text-[var(--text)]'
            )}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {oauthErrMsg && (
        <div
          role="alert"
          className="mb-6 rounded-md border border-[var(--today-fg)] bg-[var(--today-bg)] px-3 py-2 text-[0.85rem]"
          style={{ color: 'var(--today-fg)' }}
        >
          {oauthErrMsg}
        </div>
      )}

      {activeTab === 'general' && (
      <div role="tabpanel">
      <Section title="Appearance">
        <p className="mb-4 text-sm text-[var(--text-muted)]">
          Theme controls how Rialto looks. Auto follows your system.
        </p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {THEMES.map((t) => (
            <label
              key={t.id}
              className={clsx(
                'flex cursor-pointer flex-col items-stretch rounded-lg border p-2',
                theme === t.id
                  ? 'border-[var(--accent)] shadow-[0_0_0_1px_var(--accent)]'
                  : 'border-[var(--border)] hover:border-[var(--border-strong)]'
              )}
            >
              <input type="radio" className="hidden" checked={theme === t.id} onChange={() => setTheme(t.id)} />
              <span
                className="mb-2 flex h-10 w-full items-center justify-between overflow-hidden rounded border px-2"
                style={{
                  background:
                    t.id === 'auto'
                      ? 'linear-gradient(90deg, #ffffff 50%, #191919 50%)'
                      : t.bg,
                  borderColor: 'rgba(0,0,0,0.15)'
                }}
              >
                {t.id === 'auto' ? (
                  <>
                    <span className="text-xs font-medium" style={{ color: '#1f1f1e' }}>Aa</span>
                    <span className="text-xs font-medium" style={{ color: '#e6e6e3' }}>Aa</span>
                  </>
                ) : (
                  <>
                    <span className="text-xs font-medium" style={{ color: t.text }}>Aa</span>
                    <span
                      className="inline-block h-4 w-4 rounded-full"
                      style={{ background: t.accent }}
                    />
                  </>
                )}
              </span>
              <span className="text-center text-[0.78rem] text-[var(--text-muted)]">{t.name}</span>
            </label>
          ))}
        </div>
      </Section>

      <Section title="Calendar">
        <p className="mb-3 text-sm text-[var(--text-muted)]">Week starts on</p>
        <div className="mb-6 flex gap-2">
          {[
            { v: 0, label: 'Sunday' },
            { v: 1, label: 'Monday' }
          ].map(({ v, label }) => (
            <button
              key={v}
              onClick={() => updatePrefs.mutate({ week_starts_on: v })}
              disabled={updatePrefs.isPending}
              className={clsx(
                'flex-1 rounded-lg border px-4 py-3 text-sm',
                weekStartsOn === v
                  ? 'border-[var(--accent)] shadow-[0_0_0_1px_var(--accent)]'
                  : 'border-[var(--border)] hover:bg-[var(--bg-hover)]'
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <p className="mb-2 text-sm text-[var(--text-muted)]">
          Default calendar for new events (used by Quick Add and the "default" keyword)
        </p>
        <select
          value={prefsQ.data?.default_calendar_id ?? ''}
          onChange={(e) =>
            updatePrefs.mutate({
              default_calendar_id: e.target.value || null
            })
          }
          className="w-full min-w-0 rounded border border-[var(--border)] bg-[var(--bg)] px-3 py-[8px] text-sm"
        >
          <option value="">(no default — primary calendar used)</option>
          {writableCalendars.map((c) => (
            <option key={c.id} value={c.id}>
              {c.summary} — {c.account_email}
            </option>
          ))}
        </select>
      </Section>
      </div>
      )}

      {activeTab === 'calendars' && (
      <div role="tabpanel">

      <Section title="Sidebar calendars">
        <p className="mb-3 text-[0.85rem] text-[var(--text-muted)]">
          Choose which calendars appear in the sidebar. Hidden calendars stay
          synced and reachable here; their events don't show on the grid.
        </p>
        <div className="flex flex-col divide-y divide-[var(--border)] overflow-hidden rounded-lg border border-[var(--border)]">
          {(calsQ.data ?? []).map((c) => {
            const accountErrored = (accountsQ.data ?? []).some(
              (a) => a.id === c.account_id && a.status === 'error'
            );
            // Only show per-calendar issues when the account itself is fine
            // — an account-level error already explains everything below it.
            const calError = !accountErrored && c.last_error ? c.last_error : null;
            const pushState = !accountErrored
              ? calendarPushStatus(c, pushConfigured)
              : 'na';
            return (
              <label
                key={c.id}
                className="flex cursor-pointer flex-col gap-1 px-3 py-2 hover:bg-[var(--bg-hover)]"
              >
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={c.in_sidebar}
                    onChange={(ev) =>
                      setInSidebar.mutate({ id: c.id, in_sidebar: ev.target.checked })
                    }
                  />
                  <span
                    aria-hidden
                    className="block h-[12px] w-[12px] flex-shrink-0 rounded-[3px]"
                    style={{ background: retintEventBackground(c.background_color, themeName) }}
                  />
                  <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-sm">
                    {c.summary}
                  </span>
                  <span className="text-[0.78rem] text-[var(--text-faint)]">
                    {c.account_email}
                  </span>
                </div>
                {calError && (
                  <div className="ml-[44px] text-[0.78rem] text-[var(--today-fg)]">
                    {calError}
                  </div>
                )}
                {pushState === 'degraded' && (
                  <div className="ml-[44px] text-[0.78rem] text-[var(--today-fg)]">
                    Push notifications stopped — events arrive on the slow poll instead.
                  </div>
                )}
              </label>
            );
          })}
          {(calsQ.data ?? []).length === 0 && (
            <div className="px-3 py-3 text-sm text-[var(--text-faint)]">No calendars yet.</div>
          )}
        </div>
      </Section>

      <Section title="Calendar colors">
        <p className="mb-2 text-[0.85rem] text-[var(--text-muted)]">
          Pick per calendar from the sidebar swatch, or distribute hues evenly across every connected
          calendar. Stored on Google as your per-user calendar color; the active theme retints them.
        </p>
        <button
          onClick={() => setConfirmSpread(true)}
          disabled={autoSpread.isPending}
          className="rounded-lg border border-[var(--border)] px-4 py-[6px] text-[0.85rem] hover:bg-[var(--bg-hover)] disabled:opacity-50"
        >
          {autoSpread.isPending ? 'Distributing…' : 'Auto-spread calendar colors'}
        </button>
        {autoSpread.isError && (
          <div className="mt-2 text-[0.78rem] text-[var(--today-fg)]">
            {(autoSpread.error as Error).message}
          </div>
        )}
      </Section>

      <Section title="Quick Add keywords">
        <p className="mb-3 text-[0.85rem] text-[var(--text-muted)]">
          Typing one of these words in Quick Add (Space) auto-selects that calendar
          and strips the word from the title. Initial keywords are derived from each
          calendar's name; add or remove as you like.
        </p>
        <CalendarKeywordEditor />
      </Section>
      </div>
      )}

      {activeTab === 'weather' && (
        <div role="tabpanel">
          <WeatherSettings />
        </div>
      )}

      {activeTab === 'accounts' && (
      <div role="tabpanel">
      <Section title="Calendar accounts">
        <p className="mb-3 text-[0.85rem] text-[var(--text-muted)]">
          Sign-out ends this browser session. Connected accounts and your
          settings stay until you disconnect them — sign in again from any
          device to pick up where you left off.
        </p>
        {!pushConfigured && (
          // System-wide push state. Empty PUBLIC_WEBHOOK_ORIGIN on the
          // backend means no real-time delivery — local dev or a
          // misconfigured production env. Surfacing this explicitly so a
          // user (or future me) doesn't sit confused about why a Google
          // edit takes 30 minutes to appear.
          <div className="mb-3 rounded-md border border-[var(--border)] bg-[var(--bg-muted)] px-3 py-2 text-[0.78rem] text-[var(--text-muted)]">
            <span className="font-medium">Push notifications: disabled.</span>{' '}
            This server has no public origin configured for Google webhooks.
            Events arrive on the 30-minute poll instead of in real time.
          </div>
        )}
        <div className="flex flex-col gap-2">
          {(accountsQ.data ?? []).map((a) => {
            const isIdentity = a.id === identityAccountId;
            return (
              <div
                key={a.id}
                className="flex items-center justify-between rounded-lg border border-[var(--border)] px-4 py-3"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{a.display_name}</span>
                    {isIdentity && (
                      <span
                        title="The Google account you signed in with. Disconnecting won't sign you out, but you'll need to reconnect to read its calendars."
                        className="rounded-full border border-[var(--accent)] px-[6px] py-[1px] text-[0.7rem] uppercase tracking-wide text-[var(--accent)]"
                      >
                        Sign-in
                      </span>
                    )}
                  </div>
                  <div className="mt-[2px] text-[0.78rem] text-[var(--text-muted)]">
                    {a.source_account_id}
                    {a.last_synced_at && ` · synced ${format(new Date(a.last_synced_at), 'MMM d, h:mm a')}`}
                  </div>
                  {a.status !== 'active' && (
                    <div className="mt-[2px] text-[0.78rem] text-[var(--today-fg)]">
                      {a.error_message || a.status}
                    </div>
                  )}
                </div>
                {a.status === 'error' ? (
                  <a
                    href={`/google/connect?email=${encodeURIComponent(a.source_account_id)}`}
                    className="rounded-md border border-[var(--accent)] px-3 py-[6px] text-[0.8rem] text-[var(--accent)] no-underline hover:bg-[var(--accent)] hover:text-[var(--accent-fg)]"
                  >
                    Reconnect
                  </a>
                ) : (
                  <button
                    onClick={() => setDisconnectingId(a.id)}
                    className="rounded-md border border-[var(--border)] px-3 py-[6px] text-[0.8rem] text-[var(--text-muted)] hover:border-[var(--today-fg)] hover:text-[var(--today-fg)]"
                  >
                    Disconnect
                  </button>
                )}
              </div>
            );
          })}

          <a
            href="/google/connect"
            className="block rounded-lg border border-dashed border-[var(--border-strong)] py-3 text-center text-sm text-[var(--text-muted)] no-underline hover:border-[var(--accent)] hover:text-[var(--accent)]"
          >
            + Add Google account
          </a>
        </div>
      </Section>
      </div>
      )}

      <ConfirmDialog
        open={disconnectingId !== null}
        title={
          disconnectingId === identityAccountId
            ? 'Disconnect sign-in account?'
            : 'Disconnect Google account?'
        }
        message={
          disconnectingId === identityAccountId
            ? "This is the Google account you signed in with. Its calendars will be removed locally and the token revoked. You'll stay signed in until you log out, but to read this account's calendars again you'll need to reconnect it."
            : 'Its calendars and events will be removed locally. You can reconnect anytime.'
        }
        confirmLabel="Disconnect"
        danger
        onConfirm={() => {
          if (disconnectingId) disconnect.mutate(disconnectingId);
          setDisconnectingId(null);
        }}
        onClose={() => setDisconnectingId(null)}
      />
      <ConfirmDialog
        open={confirmSpread}
        title="Reassign calendar colors?"
        message="Every calendar will get an evenly-spaced hue. Saved to Google as your per-user color."
        confirmLabel="Reassign"
        onConfirm={() => {
          autoSpread.mutate();
          setConfirmSpread(false);
        }}
        onClose={() => setConfirmSpread(false)}
      />
    </div>
  );
}
